import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMagnifyingGlass,
  faPrint,
  faRotateRight,
  faTrashCan,
  faPaperPlane,
  faSpinner,
  faXmark,
  faArrowRight,
  faCircleCheck,
  faShieldHalved,
  faEye,
  faEyeSlash,
  faClipboardList,
  faDollarSign,
  faPaperclip,
  faTable,
} from "@fortawesome/free-solid-svg-icons";
import { faFileExcel, faFilePdf, faFile } from "@fortawesome/free-regular-svg-icons";
import { format } from "date-fns";
import {
  APIConstants,
  ClaimApprovalConstants,
  CommonAPIConstants,
  IntelliBillConstants,
  FilesAPIConstants,
} from "../utilities/Constants";
import { exportExcel } from "../utilities/exportToExcel";
// Styling now comes entirely from the shared Healthiva design system
// (src/styles/healthiva, imported once and globally in src/index.js) via
// hv- classes below — this page no longer ships its own stylesheet.

const DATE_TYPES = [
  { label: "DOS", value: "AppointmentDate" },
  { label: "To Approve", value: "PostingDate" },
  { label: "Submit Date", value: "SubmitDate" },
];

// Default order mirrors the old UI dropdown; the real list is loaded
// dynamically from GetClaimDropDowns → SearchByFilters when available.
const DEFAULT_SEARCH_FILTERS = [
  { label: "Patient Name", value: "pat" },
  { label: "Submitted By", value: "submittedby" },
  { label: "Updated By", value: "updatedby" },
  { label: "Insurance", value: "ins" },
];

const STATUS_PILLS = [
  { label: "All", value: "0" },
  { label: "Pending", value: "4" },
  { label: "Sent", value: "3" },
  { label: "Rejected", value: "2" },
  { label: "Paid", value: "1" },
];

// Count-card key → corresponding status pill value.
// Cards not in this map (Submitted, Nonsub, Forwarded) reset the pill to "0" (All).
const CARD_TO_PILL = {
  All: "0",
  Pending: "4",
  Sent: "3",
  Rejected: "2",
};

const fmtDate = (d) => {
  if (!d) return "";
  const date = new Date(d);
  return isNaN(date) ? "" : format(date, "MM/dd/yyyy");
};

const fmtMoney = (v) => {
  const n = Number(v);
  if (!isFinite(n)) return "";
  return `$${Number(v).toFixed(2)}`;
};

// status integers from the back-end that mean "still editable / can send"
const canSendStatus = (s) =>
  s === "" || s === null || s === undefined || s === 0 || s === "0" || s === 6 || s === "6" || s === 8 || s === "8";

// Status count card matcher — mirrors the ReloadClaimsData switch in the old code
const matchesCountCard = (c, key) => {
  switch (key) {
    case "All":       return true;
    case "Submitted": return !!c.SubmitDate || !!c.SecClaimSubmiDate;
    case "Nonsub":    return !c.SubmitDate && !c.SecClaimSubmiDate;
    case "Pending":   return String(c.ClaimStatusInt) === "4";
    case "Sent":      return String(c.ClaimStatusInt) === "3";
    case "Forwarded": return String(c.ClaimStatusInt) === "5";
    case "Rejected":  return String(c.ClaimStatusInt) === "2";
    default:          return true;
  }
};

const SortLink = ({ k, sortConfig, onClick, children }) => {
  const isSorted = sortConfig.key === k;
  const cls = isSorted
    ? `hv-sort is-sorted-${sortConfig.direction}`
    : "hv-sort";
  return (
    <span className={cls} onClick={() => onClick(k)}>{children}</span>
  );
};

const ClaimApproval = () => {
  const userData = useSelector((state) => state.user.user);
  const sAuthToken = useSelector((state) => state.user.token);
  const userPracticeID = userData ? userData.practiceID : null;
  const userId = userData ? userData.userID : null;

  // ── Filters ─────────────────────────────────────────────────
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d;
  });
  const [endDate, setEndDate] = useState(new Date());
  const [dateType, setDateType] = useState("PostingDate");
  const [searchFilter, setSearchFilter] = useState("updatedby");
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("0");
  const [selectedPatID, setSelectedPatID] = useState("");
  const [selectedInsID, setSelectedInsID] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestTimer = useRef(null);
  const tableRef = useRef(null);
  const [selectedLocation, setSelectedLocation] = useState("-1");
  const [allDates, setAllDates] = useState(false);
  const [startOpen, setStartOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);

  const handleTodayClick = (which) => {
    const today = new Date();
    if (which === "start") {
      setStartDate(today);
      setEndDate(today);
      setStartOpen(false);
    } else {
      setEndDate(today);
      setEndOpen(false);
    }
  };

  // Same date-picker recipe as IntelliBill.js: getMonths() hides months past
  // "today" for the current year (matches maxDate={new Date()}), and the
  // calendar container is a plain inline-styled box instead of a custom
  // stylesheet class — that's what made IntelliBill's popup reliably render,
  // so re-used here as-is instead of continuing to patch a custom theme.
  const getMonths = (selectedYear) => {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();
    return Array.from({ length: 12 }, (v, k) => {
      if (selectedYear === currentYear && k > currentMonth) {
        return null;
      }
      return (
        <option key={k} value={k}>
          {format(new Date(selectedYear, k), "MMMM")}
        </option>
      );
    }).filter((option) => option !== null);
  };

  const renderCalendarHeader = ({
    date, changeYear, changeMonth, decreaseMonth, increaseMonth,
    prevMonthButtonDisabled, nextMonthButtonDisabled,
  }) => {
    const d = date instanceof Date && !isNaN(date) ? date : new Date();
    const selectedMonth = d.getMonth();
    const selectedYear = d.getFullYear();
    return (
      // Styled via .hv-cal-header / .hv-cal-nav-btn / .hv-cal-select, shared
      // in styles/healthiva/_forms.scss so every page's DatePicker popup
      // gets the same header instead of bare browser controls.
      <div className="hv-cal-header">
        <button
          type="button"
          className="hv-cal-nav-btn"
          onClick={decreaseMonth}
          disabled={prevMonthButtonDisabled}
        >
          {"<"}
        </button>
        <select
          className="hv-cal-select"
          value={selectedMonth}
          onChange={({ target: { value } }) => changeMonth(Number(value))}
        >
          {getMonths(selectedYear)}
        </select>
        <select
          className="hv-cal-select"
          value={selectedYear}
          onChange={({ target: { value } }) => changeYear(Number(value))}
        >
          {Array.from({ length: 100 }, (v, k) => (
            <option key={k} value={new Date().getFullYear() - k}>
              {new Date().getFullYear() - k}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="hv-cal-nav-btn"
          onClick={increaseMonth}
          disabled={nextMonthButtonDisabled}
        >
          {">"}
        </button>
      </div>
    );
  };

  const makeCalContainer = (which) => ({ children }) => (
    <div className="hv-cal-container">
      {children}
      {/* IntelliBill.js centers this footer with a fixed marginLeft: "65px"
          instead of an actual flex container — that only happens to line up
          for its own calendar width, so on our grid (a different width) the
          Today button was landing off-center. display:flex + justifyContent
          centers it for real regardless of the calendar's width. */}
      <div className="hv-cal-footer">
        <button type="button" className="today-btn" onClick={() => handleTodayClick(which)}>
          Today
        </button>
      </div>
    </div>
  );

  const handleAllDatesToggle = (e) => {
    const checked = e.target.checked;
    setAllDates(checked);
    if (checked) {
      setStartDate(new Date("2015-01-01"));
      setEndDate(new Date());
    } else {
      const d = new Date();
      d.setDate(d.getDate() - 7);
      setStartDate(d);
      setEndDate(new Date());
    }
  };

  // ── Data ────────────────────────────────────────────────────
  const [claims, setClaims] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [clearingHouses, setClearingHouses] = useState([]);
  const [searchFilters, setSearchFilters] = useState(DEFAULT_SEARCH_FILTERS);
  const [statusPills, setStatusPills] = useState(STATUS_PILLS);

  // ── Table interaction state ────────────────────────────────
  const [columnSearch, setColumnSearch] = useState({
    dos: "", patient: "", dob: "", insurance: "",
    status: "", claim: "", provider: "", cpt: "",
    icd: "", modifier: "",
    totalCharge: "", submitDate: "", receivedCharge: "",
    updatedBy: "", submittedBy: "",
  });
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });
  const [activeTab, setActiveTab] = useState("details");
  const [viewLimit, setViewLimit] = useState("all"); // 25 / 50 / 75 / 100 / all — doubles as page size
  const [currentPage, setCurrentPage] = useState(1);
  const [activeRowIndex, setActiveRowIndex] = useState(null); // highlighted row on click
  const [selectedRows, setSelectedRows] = useState([]);      // delete checkbox
  const [selectedSendRows, setSelectedSendRows] = useState([]); // send checkbox
  const [insuranceChecked, setInsuranceChecked] = useState([]); // eligibility checkboxes
  const [rowState, setRowState] = useState({});               // per-claim regenerate/eligibility loading
  const [activeCount, setActiveCount] = useState("All");

  // ── Modals ──────────────────────────────────────────────────
  const [confirmSendOpen, setConfirmSendOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [selectErrorOpen, setSelectErrorOpen] = useState(false);
  const [sendSuccessOpen, setSendSuccessOpen] = useState(false);
  const [sendSuccessCount, setSendSuccessCount] = useState(0);
  const [deleteSuccessOpen, setDeleteSuccessOpen] = useState(false);
  const [deleteSuccessCount, setDeleteSuccessCount] = useState(0);
  const [epinInvalidOpen, setEpinInvalidOpen] = useState(false);
  const [eventsModal, setEventsModal] = useState({ open: false, claimId: "", events: [] });
  const [attachmentsModal, setAttachmentsModal] = useState({ open: false, claimId: "", rows: [] });

  // EPIN (delete gate)
  const [epin, setEpin] = useState("");
  const [showEpin, setShowEpin] = useState(false);

  useEffect(() => { document.title = "Claim Approval"; }, []);

  // ── Fetch: dropdowns ────────────────────────────────────────
  useEffect(() => {
    if (!userData) return;
    const body = JSON.stringify({ sInputJSON: JSON.stringify(userData) });
    fetch(APIConstants.BASE_URL + ClaimApprovalConstants.GetClaimDropDowns, {
      method: "POST",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body,
    })
      .then((res) => res.json())
      .then((data) => {
        const parsed = JSON.parse(data.d);
        const dd = parsed.Data || {};
        // Debug: open browser console to see exact response shape if data is missing
        console.log("[ClaimApprovalV2] GetClaimDropDowns →", dd);
        setLocations(dd.LocationDetails || []);

        // Try multiple possible field names since the API response shape may
        // differ between environments. We DO NOT override searchFilter here —
        // it stays at its default "updatedby" so the <select> binds to a
        // stable value and the API options just sync to it (matches OLD UI).
        const sbf = dd.SearchByFilters || dd.searchByFilters || [];
        if (Array.isArray(sbf) && sbf.length) {
          const opts = sbf
            .map((f) => ({
              label: f.Label ?? f.label ?? f.Text ?? "",
              value: f.sFilterType ?? f.FilterType ?? f.Value ?? f.value ?? "",
            }))
            .filter((o) => o.label && o.value);
          if (opts.length > 0) setSearchFilters(opts);
        }

        const srb = dd.StatusRadioButtons || dd.statusRadioButtons || [];
        if (Array.isArray(srb) && srb.length) {
          const pillOpts = srb
            .map((s) => ({
              label: s.Label ?? s.label ?? s.Text ?? "",
              value: String(s.ClaimStatus ?? s.Value ?? s.value ?? ""),
            }))
            .filter((o) => o.label !== "");
          if (pillOpts.length > 0) {
            setStatusPills(pillOpts);
            const allPill = pillOpts.find((p) => /^all$/i.test(p.label)) || pillOpts[0];
            if (allPill) setStatusFilter(allPill.value);
          }
        }
      })
      .catch((err) => console.error("GetClaimDropDowns failed:", err));
  }, [userData]);

  // ── Fetch: clearing house list ─────────────────────────────
  useEffect(() => {
    if (!userPracticeID) return;
    const body = JSON.stringify({ sInputJSON: JSON.stringify({ sPracticeID: userPracticeID }) });
    fetch(APIConstants.BASE_URL + CommonAPIConstants.GetAllclearingHouse, {
      method: "POST",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body,
    })
      .then((res) => res.json())
      .then((data) => {
        const parsed = JSON.parse(data.d);
        setClearingHouses(parsed.Data || []);
      })
      .catch((err) => console.error("GetAllclearingHouse failed:", err));
  }, [userPracticeID]);

  // ── Fetch: claims ───────────────────────────────────────────
  const fetchClaims = useCallback(async () => {
    if (!userPracticeID) return;
    setLoading(true);
    const objInput = {
      iPracticeID: String(userPracticeID),
      sFromDate: fmtDate(startDate),
      sToDate: fmtDate(endDate),
      sFilterType: searchFilter,
      sPatID: String(selectedPatID || userId || ""),
      sInsID: String(selectedInsID || ""),
      sDefaultFilter: dateType,
      sLocId: selectedLocation === "-1" ? "0" : String(selectedLocation),
      sFilterText: searchText || userData?.userFullName || "",
      sClaimStatus: statusFilter,
      LoggedInUserDetails: { practiceID: userPracticeID },
    };
    const inputJSON = JSON.stringify({ objClaim: objInput });
    const url = `${APIConstants.BASE_URL_BILLING}${ClaimApprovalConstants.GetApprovalPageClaims}?sInputJSON=${inputJSON}`;
    console.log("[ClaimApprovalV2] fetchClaims request →", objInput);
    try {
      const res = await fetch(url, {
        method: "GET",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
      });
      if (!res.ok) throw new Error(`status ${res.status}`);
      const data = await res.json();
      console.log("[ClaimApprovalV2] fetchClaims response →", Array.isArray(data) ? `${data.length} rows` : data);
      setClaims(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("GetApprovalPageClaims failed:", err);
      setClaims([]);
    } finally {
      setLoading(false);
    }
  }, [userPracticeID, userId, userData, startDate, endDate, searchFilter, searchText, dateType, statusFilter, selectedLocation, selectedPatID, selectedInsID]);

  // ── Fetch: counts ───────────────────────────────────────────
  const fetchCounts = useCallback(async () => {
    if (!userPracticeID) return;
    const objInput = {
      iPracticeID: userPracticeID,
      sFromDate: fmtDate(startDate),
      sToDate: fmtDate(endDate),
      sDateFiterType: "1",
      sOtherFilterType: searchFilter,
      sFilterText: String(userId || ""),
      sCPT: "",
      sLocId: 0,
    };
    const sInputJSON = JSON.stringify({ objClaim: objInput });
    const url = `${APIConstants.BASE_URL_BILLING}${ClaimApprovalConstants.GetPracticeClaimStatusCount}?sInputJSON=${sInputJSON}`;
    try {
      const res = await fetch(url, {
        method: "GET",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
      });
      if (!res.ok) throw new Error(`status ${res.status}`);
      const data = await res.json();
      const parsed = typeof data === "string" ? JSON.parse(data) : data;
      const row = Array.isArray(parsed) ? parsed[0] : parsed;
      setCounts(row || {});
    } catch (err) {
      console.error("GetPracticeClaimStatusCount failed:", err);
      setCounts({});
    }
  }, [userPracticeID, userId, startDate, endDate, searchFilter]);

  // Pre-fill the search input with the logged-in user's full name —
  // matches OLD UI behaviour (ClaimApproval.js sets it unconditionally when
  // userData is present). The backend's "Updated By" filter expects this string.
  useEffect(() => {
    if (userData?.userFullName) {
      setSearchText(userData.userFullName);
    }
  }, [userData?.userFullName]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initial fetch — fires once when the required state is ready.
  // fetchClaims has a userFullName fallback so searchText doesn't need to be
  // set yet.
  const initialFetchRef = useRef(false);
  useEffect(() => {
    if (initialFetchRef.current) return;
    if (
      !userPracticeID ||
      searchFilter == null ||
      statusFilter == null
    ) return;
    initialFetchRef.current = true;
    fetchClaims();
    fetchCounts();
  }, [userPracticeID, searchFilter, statusFilter, fetchClaims, fetchCounts]);

  // Re-fetch when the user changes filter pills or status (after initial fetch).
  // Note: typing in the search box does NOT trigger a fetch — user clicks the
  // magnifying glass button (which calls handleSearch) instead.
  useEffect(() => {
    if (!initialFetchRef.current) return;
    fetchClaims();
    fetchCounts();
  }, [searchFilter, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = () => { fetchClaims(); fetchCounts(); };

  // Look up the pill value by its label (case-insensitive). Falls back to "0" (All).
  const pillValueByLabel = (label) => {
    const found = statusPills.find((p) => p.label.toLowerCase() === String(label).toLowerCase());
    return found ? found.value : "0";
  };

  // Card-key labels that have a 1:1 pill counterpart
  const CARD_LABELS_WITH_PILL = new Set(["All", "Pending", "Sent", "Rejected", "Paid"]);

  // Click a status pill: sync activeCount. The useEffect on statusFilter refetches.
  const applyStatusPill = (value) => {
    setStatusFilter(value);
    const pill = statusPills.find((p) => p.value === value);
    const matchingCard = pill && CARD_LABELS_WITH_PILL.has(pill.label) ? pill.label : "All";
    setActiveCount(matchingCard);
  };

  // Click a count card: sync the status pill. The useEffect on statusFilter refetches.
  const applyCountCard = (key) => {
    setActiveCount(key);
    const pillValue = CARD_LABELS_WITH_PILL.has(key) ? pillValueByLabel(key) : pillValueByLabel("All");
    setStatusFilter(pillValue);
  };

  // ── Autocomplete suggestions ────────────────────────────────
  const fetchSuggestions = useCallback(async (text) => {
    if (!text || text.length < 1) {
      setSuggestions([]);
      return;
    }

    try {
      if (searchFilter === "pat") {
        const sJSONInput = JSON.stringify({
          PracticeID: userPracticeID,
          SearchKey: text,
          SearchBy: 0,
          isFromLogin: 0,
        });
        const url = `${APIConstants.BASE_URL_SEARCH}${CommonAPIConstants.GetAllPatientNames}?sJSONInput=${sJSONInput}`;
        const res = await fetch(url);
        if (!res.ok) return;
        const data = await res.json();
        const parsed = typeof data === "string" ? JSON.parse(data) : data;
        const list = parsed?.Data;
        if (Array.isArray(list)) {
          setSuggestions(list.map((it) => ({
            label: `${it.LastName}, ${it.FirstName}`,
            val: it.PatientID,
            type: "pat",
          })));
        } else {
          setSuggestions([]);
        }
      } else if (searchFilter === "ins") {
        const sJSONInput = JSON.stringify({ SearchKey: text });
        const url = `${APIConstants.BASE_URL_SEARCH}${CommonAPIConstants.GetInsuCompanyNames}?sJSONInput=${sJSONInput}`;
        const res = await fetch(url);
        if (!res.ok) return;
        const data = await res.json();
        const parsed = typeof data === "string" ? JSON.parse(data) : data;
        const list = parsed?.Data;
        if (Array.isArray(list)) {
          setSuggestions(list.map((it) => ({
            label: it.Insurance,
            val: it.InsuID,
            type: "ins",
          })));
        } else {
          setSuggestions([]);
        }
      } else if (searchFilter === "updatedby" || searchFilter === "submittedby") {
        const body = JSON.stringify({
          sInputJSON: JSON.stringify({
            sPracticeID: userPracticeID,
            sUserID: userId,
            srchKey: text,
          }),
        });
        const res = await fetch(
          APIConstants.BASE_URL + ClaimApprovalConstants.GetSuggestionDropDowns,
          {
            method: "POST",
            headers: { Accept: "application/json", "Content-Type": "application/json" },
            body,
          }
        );
        if (!res.ok) return;
        const data = await res.json();
        const parsed = JSON.parse(data.d);
        const list = parsed?.Data;
        if (Array.isArray(list)) {
          setSuggestions(list.map((s) => {
            const [val, label] = (s || "").split("$");
            return { label, val, type: searchFilter };
          }));
        } else {
          setSuggestions([]);
        }
      } else {
        setSuggestions([]);
      }
    } catch (err) {
      console.error("Suggestion fetch failed:", err);
      setSuggestions([]);
    }
  }, [searchFilter, userPracticeID, userId]);

  const handleSearchTextChange = (text) => {
    setSearchText(text);
    setShowSuggestions(true);
    // reset pat/ins ID when user edits the text (avoids stale filter)
    setSelectedPatID("");
    setSelectedInsID("");
    if (suggestTimer.current) clearTimeout(suggestTimer.current);
    suggestTimer.current = setTimeout(() => fetchSuggestions(text), 250);
  };

  const handleSuggestionSelect = (item) => {
    setSearchText(item.label || "");
    setSuggestions([]);
    setShowSuggestions(false);
    if (item.type === "pat") {
      setSelectedPatID(item.val);
      setSelectedInsID("");
    } else if (item.type === "ins") {
      setSelectedInsID(item.val);
      setSelectedPatID("");
    } else {
      // updated by / submitted by — set as filter text only; back-end uses sFilterText
      setSelectedPatID("");
      setSelectedInsID("");
    }
  };

  const handleColumnSearch = (key, value) =>
    setColumnSearch((prev) => ({ ...prev, [key]: value }));

  const filteredClaims = useMemo(() => {
    const m = (val, q) =>
      !q || (val ?? "").toString().toLowerCase().includes(q.toLowerCase());
    return claims.filter((c) =>
      matchesCountCard(c, activeCount) &&
      m(fmtDate(c.AppointmentDate), columnSearch.dos) &&
      m(`${c.LastName}, ${c.FirstName}`, columnSearch.patient) &&
      m(fmtDate(c.DOB), columnSearch.dob) &&
      m(c.Payer || c.SecPayer, columnSearch.insurance) &&
      m(c.ClaimStatus, columnSearch.status) &&
      m(c.ClaimId, columnSearch.claim) &&
      m(c.RenderingProvider, columnSearch.provider) &&
      m(c.EncCPT, columnSearch.cpt) &&
      m(c.EncICD, columnSearch.icd) &&
      m(c.EncModifier, columnSearch.modifier) &&
      m(c.ClaimTotalSubmitedCharge, columnSearch.totalCharge) &&
      m(fmtDate(c.IsPrimarySkipped ? c.SecClaimSubmiDate : c.SubmitDate), columnSearch.submitDate) &&
      m(fmtDate(c.IsPrimarySkipped ? c.SecClaimPostingDate : c.PostingDate), columnSearch.PostingDate) &&
      m(c.ReceivedCharge, columnSearch.receivedCharge) &&
      m(c.UpdatedBy, columnSearch.updatedBy) &&
      m(c.SubmittedBy, columnSearch.submittedBy)
    );
  }, [claims, columnSearch, activeCount]);

  const sortedClaims = useMemo(() => {
    if (!sortConfig.key) return filteredClaims;
    const { key, direction } = sortConfig;
    return [...filteredClaims].sort((a, b) => {
      let av = a[key]; let bv = b[key];
      if (av == null) av = ""; if (bv == null) bv = "";
      if (!isNaN(av) && !isNaN(bv)) {
        av = parseFloat(av); bv = parseFloat(bv);
      } else {
        av = av.toString().toUpperCase(); bv = bv.toString().toUpperCase();
      }
      if (av < bv) return direction === "asc" ? -1 : 1;
      if (av > bv) return direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredClaims, sortConfig]);

  // Page size mirrors the "Show" toggle (25/50/75/100); "all" means one page
  // holding every row.
  const pageSize = viewLimit === "all" ? Math.max(sortedClaims.length, 1) : Number(viewLimit);
  const totalPages = viewLimit === "all" ? 1 : Math.max(1, Math.ceil(sortedClaims.length / pageSize));

  // Reset to page 1 whenever the filtered/sorted result set or page size changes,
  // so pagination never gets stuck past the end of a shorter result set.
  useEffect(() => {
    setCurrentPage(1);
  }, [sortedClaims, viewLimit]);

  // Clicking a row highlights it; clear that highlight when the page or tab
  // changes so it never points at a row that's no longer visible.
  useEffect(() => {
    setActiveRowIndex(null);
  }, [activeTab, currentPage]);

  const displayedClaims = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedClaims.slice(start, start + pageSize);
  }, [sortedClaims, currentPage, pageSize]);

  // Totals across the currently displayed rows (respects sort, filter, and
  // view-limit slice). Shown in the Charges & Dates tab footer.
  const totals = useMemo(() => {
    return displayedClaims.reduce(
      (acc, c) => {
        acc.totalCharge += Number(c.ClaimTotalSubmitedCharge) || 0;
        acc.receivedCharge += Number(c.ReceivedCharge) || 0;
        return acc;
      },
      { totalCharge: 0, receivedCharge: 0 }
    );
  }, [displayedClaims]);

  const handleSort = (key) =>
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));

  // ── Row selection toggles ──────────────────────────────────
  const toggleArr = (setter) => (i) =>
    setter((p) => (p.includes(i) ? p.filter((x) => x !== i) : [...p, i]));
  const toggleSelect       = toggleArr(setSelectedRows);
  const toggleSend         = toggleArr(setSelectedSendRows);
  const toggleInsurance    = toggleArr(setInsuranceChecked);

  const toggleAll = (current, setter) =>
    sortedClaims.length && sortedClaims.length === current.length
      ? setter([])
      : setter(sortedClaims.map((_, i) => i));

  // ── Per-row clearing house change (mutates local list) ─────
  const handleClearingHouseChange = (idx, value) => {
    setClaims((prev) => {
      const next = [...prev];
      const realIdx = prev.findIndex(
        (c) => c.ClaimId === sortedClaims[idx]?.ClaimId
      );
      if (realIdx >= 0) {
        next[realIdx] = { ...next[realIdx], ClearingHouse: value };
      }
      return next;
    });
  };

  // ── Eligibility per row ────────────────────────────────────
  const handleCheckEligibility = async () => {
    if (insuranceChecked.length === 0) {
      setSelectErrorOpen(true);
      return;
    }
    for (const idx of insuranceChecked) {
      const item = sortedClaims[idx];
      if (!item) continue;
      setRowState((p) => ({ ...p, [item.ClaimId]: { ...p[item.ClaimId], eligLoading: true } }));
      const input = {
        PatientID: item.PatientID,
        PracticeID: userPracticeID,
        PolicyNumber: String(item.PolicyNumber || ""),
        DOS: fmtDate(item.AppointmentDate),
        RowIndex: idx,
      };
      const sInput = JSON.stringify({ objPatientDetails: input });
      const url = `${APIConstants.PATIENT_PORTAL_URL}${CommonAPIConstants.GetPatientEligibility}?sInputJSON=${sInput}`;
      try {
        const res = await fetch(url, {
          method: "GET",
          headers: { Accept: "application/json", "Content-Type": "application/json" },
        });
        if (!res.ok) throw new Error(`status ${res.status}`);
        const data = await res.json();
        const parsed = typeof data === "string" ? JSON.parse(data) : data;
        setRowState((p) => ({
          ...p,
          [item.ClaimId]: {
            eligLoading: false,
            eligStatus: parsed?.InsStatus || "",
          },
        }));
      } catch (err) {
        console.error("Eligibility failed:", err);
        setRowState((p) => ({ ...p, [item.ClaimId]: { ...p[item.ClaimId], eligLoading: false } }));
      }
    }
  };

  // ── Send-to-CH validation ───────────────────────────────────
  // Classifies each selected row into valid (ok to send) or invalid (blocked).
  // Returns a summary including total charge and grouping by clearing house.
  const sendSummary = useMemo(() => {
    const valid = [];
    const invalid = [];
    let totalCharge = 0;
    const byCH = {};

    for (const idx of selectedSendRows) {
      const item = sortedClaims[idx];
      if (!item) continue;
      const reasons = [];
      if (item.DataValidationMessage && item.DataValidationMessage.trim() !== "") {
        reasons.push("validation errors");
      }
      if (!item.ClearingHouse || String(item.ClearingHouse).trim() === "") {
        reasons.push("no clearing house selected");
      }
      if (!canSendStatus(item.ClaimStatusInt)) {
        reasons.push(`status (${item.ClaimStatus || "unsendable"})`);
      }
      if (reasons.length > 0) {
        invalid.push({ idx, item, reasons });
      } else {
        valid.push({ idx, item });
        totalCharge += Number(item.ClaimTotalSubmitedCharge) || 0;
        const chName = (clearingHouses.find((c) => String(c.MCHID) === String(item.ClearingHouse))?.ClearingHouseName) || "Unknown";
        if (!byCH[chName]) byCH[chName] = { count: 0, total: 0 };
        byCH[chName].count += 1;
        byCH[chName].total += Number(item.ClaimTotalSubmitedCharge) || 0;
      }
    }

    return { valid, invalid, totalCharge, byCH };
  }, [selectedSendRows, sortedClaims, clearingHouses]);

  // ── Send to clearing house ─────────────────────────────────
  const openSend = () => {
    if (selectedSendRows.length === 0) { setSelectErrorOpen(true); return; }
    setConfirmSendOpen(true);
  };

  const handleSendToCH = async () => {
    if (sendSummary.valid.length === 0) {
      // nothing to send — close the modal
      setConfirmSendOpen(false);
      return;
    }
    const payload = sendSummary.valid.map(({ item }) => ({
      PracticeID: String(userPracticeID),
      PatientID: String(item.PatientID),
      ClaimID: item.ClaimId,
      DOS: fmtDate(item.AppointmentDate),
      EncounterID: String(item.Encounter || ""),
      PatientName: `${item.FirstName || ""}${item.LastName || ""}`,
      isPriorSecClaim: String(item.PriorSecClaim || ""),
      Status:item.Status || 0,
      iClearingHouse: item.ClearingHouse,
      userId,
      userpracticeId: userPracticeID,
    }));
    const body = JSON.stringify(JSON.stringify({ objClaim: payload }));
    try {
      const res = await fetch(APIConstants.BASE_URL_BILLING + ClaimApprovalConstants.SaveApprovalClaims, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${sAuthToken}`,
        },
        body,
      });
      if (res.ok) {
        setSendSuccessCount(sendSummary.valid.length);
        setConfirmSendOpen(false);
        setSendSuccessOpen(true);
        setSelectedSendRows([]);
        fetchClaims();
      } else {
        console.error("SaveApprovalClaims status:", res.status);
      }
    } catch (err) {
      console.error("SaveApprovalClaims failed:", err);
    }
  };

  // ── Delete (EPIN-gated) ────────────────────────────────────
  const openDelete = () => {
    if (selectedRows.length === 0) { setSelectErrorOpen(true); return; }
    setEpin("");
    setConfirmDeleteOpen(true);
  };

  const verifyEpinAndGet = async () => {
    const sInputJSON = JSON.stringify({ UserID: userData?.userID, EPIN: epin });
    const url = `${APIConstants.SERVICES_URL}${IntelliBillConstants.GetVerifyEPIN}?sInputJSON=${sInputJSON}`;
    try {
      const res = await fetch(url, {
        method: "GET",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
      });
      if (!res.ok) return 0;
      const data = await res.json();
      return Number(data);
    } catch {
      return 0;
    }
  };

  const handleDelete = async (isPermanent) => {
    const verified = await verifyEpinAndGet();
    if (verified !== 1) {
      setConfirmDeleteOpen(false);
      setEpinInvalidOpen(true);
      return;
    }
    const objClaim = selectedRows.map((idx) => {
      const item = sortedClaims[idx];
      return {
        PracticeID: userPracticeID,
        PatientID: String(item.PatientID),
        ClaimID: item.ClaimId,
        EncounterID: 0,
        PatientName: `${item.LastName},${item.FirstName}`,
        DOS: fmtDate(item.AppointmentDate),
        IsDeletePermanently: isPermanent,
        UserID: userId,
        UserFullName: userData?.userFullName,
      };
    });
    const body = JSON.stringify(JSON.stringify({ objClaim }));
    try {
      const res = await fetch(APIConstants.BASE_URL_BILLING + CommonAPIConstants.DeleteClaim, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${sAuthToken}`,
        },
        body,
      });
      if (res.ok) {
        setDeleteSuccessCount(selectedRows.length);
        setConfirmDeleteOpen(false);
        setDeleteSuccessOpen(true);
        setSelectedRows([]);
        setEpin("");
        fetchClaims();
      } else {
        console.error("DeleteClaim status:", res.status);
      }
    } catch (err) {
      console.error("DeleteClaim failed:", err);
    }
  };

  // ── Claim events timeline ──────────────────────────────────
  const openEvents = async (item) => {
    const inputJSON = {
      PracticeID: userPracticeID,
      ModuleName: "Billing",
      dtFromDate: fmtDate(startDate),
      dtToDate: fmtDate(endDate),
      DataID: item.ClaimId,
      UserID: 0,
      DateType: "DOS",
    };
    const sInputJSON = JSON.stringify(inputJSON);
    const url = `${APIConstants.COMMON_BASE_URL}${CommonAPIConstants.GetClaimAllEvents}?sJSONInput=${sInputJSON}`;
    try {
      const res = await fetch(url, {
        method: "GET",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
      });
      if (!res.ok) return;
      const data = await res.json();
      const parsed = typeof data === "string" ? JSON.parse(data) : data;
      setEventsModal({ open: true, claimId: item.ClaimId, events: parsed?.Data || [] });
    } catch (err) {
      console.error("GetClaimAllEvents failed:", err);
    }
  };

  // ── Attachments ────────────────────────────────────────────
  const openAttachments = async (item) => {
    const sInputJSON = JSON.stringify({ ClaimID: item.ClaimId, PracticeID: userPracticeID });
    const url = `${APIConstants.BASE_URL_BILLING}${CommonAPIConstants.GetClaimAttachments}?sInputJSON=${sInputJSON}`;
    try {
      const res = await fetch(url, {
        method: "GET",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
      });
      if (!res.ok) return;
      const data = await res.json();
      setAttachmentsModal({ open: true, claimId: item.ClaimId, rows: Array.isArray(data) ? data : [] });
    } catch (err) {
      console.error("GetClaimAttachments failed:", err);
    }
  };

  // ── CMS-1500 PDF ───────────────────────────────────────────
  const openCMSPDF = async (item) => {
    const sInputJSON = JSON.stringify({
      PracticeID: userPracticeID, ClaimID: item.ClaimId, PatientID: item.PatientID,
    });
    const url = `${APIConstants.BASE_URL_BILLING}${CommonAPIConstants.GetPDFCMS1500}?sInputJSON=${sInputJSON}`;
    try {
      const res = await fetch(url, { method: "GET", headers: { Accept: "application/json" } });
      const data = await res.json();
      const parsed = typeof data === "string" ? JSON.parse(data) : data;
      if (parsed?.DocumentURL) window.open(parsed.DocumentURL, "_blank");
    } catch (err) {
      console.error("GetPDFCMS1500 failed:", err);
    }
  };

  const regenerateCMSPDF = async (item) => {
    setRowState((p) => ({ ...p, [item.ClaimId]: { ...p[item.ClaimId], regenLoading: true } }));
    const objInput = {
      PatientID: item.PatientID,
      ClaimID: item.ClaimId,
      PracticeID: userPracticeID,
      PriorSecClaim: item.PriorSecClaim,
      UserTimeZoneOffSet: 300,
      UserID: userId,
    };
    const sInputJSON = JSON.stringify({ objClaim: objInput });
    const url = `${APIConstants.BASE_URL_BILLING}${CommonAPIConstants.SaveRegeneratePDFCMS1500}?sInputJSON=${sInputJSON}`;
    try {
      await fetch(url, { method: "POST", headers: { Accept: "application/json" } });
    } catch (err) {
      console.error("RegeneratePDF failed:", err);
    } finally {
      setRowState((p) => ({ ...p, [item.ClaimId]: { ...p[item.ClaimId], regenLoading: false } }));
    }
  };

  // ── Exports (Excel / PDF / Print) ──────────────────────────
  const getClearingHouseName = (id) => {
    const found = clearingHouses.find((ch) => String(ch.MCHID) === String(id));
    return found ? found.ClearingHouseName : "";
  };

  const formatInsuranceCell = (item) => {
    const isSecondary = !!item.SecPayerID;
    const payer  = isSecondary ? item.SecPayer  : item.Payer;
    const payerId = isSecondary ? item.SecPayerID : item.PayerID;
    const policy = isSecondary ? item.SecInsPolicyNumber : item.PolicyNumber;
    return `${payer || ""}\n${payerId || ""}\n${policy || ""}`;
  };

  const exportToExcel = () => {
    const rows = displayedClaims.map((item, index) => ({
      "#": index + 1,
      Location: item.LocationNickname || "",
      DOS: fmtDate(item.AppointmentDate) || "",
      "Patient Name": `${item.LastName || ""}, ${item.FirstName || ""}`,
      DOB: fmtDate(item.DOB) || "",
      Insurance: formatInsuranceCell(item),
      Status: item.ClaimStatus || "",
      "Claim #": item.ClaimId || "",
      Provider: item.RenderingProvider || "",
      CPT: item.EncCPT ? item.EncCPT.replace(/^,/, "") : "",
      ICD: item.EncICD || "",
      Modifier: item.EncModifier || "",
      "Total Charge": item.ClaimTotalSubmitedCharge ? `$${item.ClaimTotalSubmitedCharge}` : "",
      "Posting Date": fmtDate(item.PostingDate) || "",
      "Submit Date": fmtDate(item.IsPrimarySkipped ? item.SecClaimSubmiDate : item.SubmitDate),
      "Received Charge": item.ReceivedCharge ?? "",
      "Clearing House": getClearingHouseName(item.ClearingHouse),
      Send: canSendStatus(item.ClaimStatusInt) ? "Yes" : "No",
      "Updated By": item.UpdatedBy || "",
      "Submitted By": item.SubmittedBy || "",
      Attachments: item.Attachments || "",
      Validate: item.DataValidationMessage ? "Invalid" : "Yes",
    }));
    exportExcel(rows, "Approval", "Approval");
  };

  const exportToPDF = () => {
    if (!tableRef.current) return;
    const tableHTML = tableRef.current.outerHTML;
    const PrintableDataDetails = {
      PrintableData: tableHTML,
      PageName: "Claim Approval",
    };
    const InputJSONLevelOne = JSON.stringify(PrintableDataDetails);
    const InputJSON = JSON.stringify(InputJSONLevelOne);
    fetch(APIConstants.SERVICES_URL + FilesAPIConstants.ExportPdfFile, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: InputJSON,
    })
      .then((res) => res.json())
      .then((data) => {
        if (data && data[2]) {
          window.open(data[2], "_blank");
        }
      })
      .catch((err) => console.error("Export to PDF failed:", err));
  };

  const handlePrint = () => {
    if (!tableRef.current) {
      window.print();
      return;
    }
    const tableHTML = tableRef.current.outerHTML;
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;
    printWindow.document.writeln(`
      <html>
        <head>
          <title>Claim Approval</title>
          <style>
            body { font-family: 'Segoe UI', Arial, sans-serif; margin: 14px; color: #000; }
            h1 { text-align: center; color: #1a9b8c; font-size: 18px; margin-bottom: 12px; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #c0d8d4; padding: 4px 6px; font-size: 10px; text-align: left; vertical-align: top; }
            th { background: #e6f6f4; color: #0e6b60; font-weight: 700; text-transform: uppercase; letter-spacing: .3px; }
            tr:nth-child(even) td { background: #f7fbfa; }
            .hv-th-search, .hv-th-check, .hv-th-check-btn, .hv-th-trash, button { display: none !important; }
            input[type="checkbox"], select { display: none !important; }
            /* .hv-td-link is a real <button> (see _tables.scss) so the
               generic button-hide rule above would otherwise blank out every
               patient/claim link in the printed table. */
            .hv-td-link { display: inline !important; color: #1a9b8c; text-decoration: none; border: 0; background: none; padding: 0; font: inherit; }
            .svg-inline--fa,
            .hv-td-link svg,
            .hv-table td svg,
            .hv-table th svg {
              width: 12px !important;
              height: 12px !important;
              font-size: 12px !important;
              line-height: 1 !important;
              vertical-align: middle !important;
            }
            @media print {
              @page { size: A3 landscape; margin: 10mm; }
              body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            }
          </style>
        </head>
        <body>
          <h1>Claim Approval — ${fmtDate(startDate)} to ${fmtDate(endDate)}</h1>
          ${tableHTML}
          <script>
            window.onload = function() { window.print(); };
            window.onafterprint = function() { window.close(); };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // ── Link openers ───────────────────────────────────────────
  const openDOSLink = (item) => {
    localStorage.setItem("selectedClaimItem", JSON.stringify(item));
    window.open(`/#/transactionlogs/${item.ClaimId}`, "_blank");
  };
  const openPatLink = (item) => {
    localStorage.setItem("PatientID", item.PatientID);
    window.open(`/#/patientdashboard/${item.PatientID}/primary-insurance`, "_blank");
  };
  const openClaimLink = (item) =>
    window.open(`/#/new-encounter/${item.ClaimId}`, "_blank");

  // ── Count cards ────────────────────────────────────────────
  const countCards = [
    { key: "All",       label: "All Claims",    value: counts.AllClaims ?? sortedClaims.length },
    { key: "Submitted", label: "Submitted",     value: counts.Submitted ?? 0 },
    { key: "Nonsub",    label: "Non-submitted", value: counts.Nonsubmitted ?? 0 },
    { key: "Pending",   label: "Pending",       value: counts.PendingClaims ?? 0 },
    { key: "Sent",      label: "Sent",          value: counts.SentClaims ?? 0 },
    { key: "Forwarded", label: "Forwarded",     value: counts.ForwrdedClaims ?? 0 },
    { key: "Rejected",  label: "Rejected",      value: counts.RejectedClaims ?? 0 },
  ];

  const statusBadgeClass = (status) => {
    const s = (status || "").toLowerCase();
    if (s.includes("paid")) return "hv-pill hv-pill--paid";
    if (s.includes("pend")) return "hv-pill hv-pill--pending";
    if (s.includes("sent")) return "hv-pill hv-pill--info";
    if (s.includes("reject")) return "hv-pill hv-pill--denied";
    return "hv-pill hv-pill--neutral";
  };

  const closeAllModals = () => {
    setConfirmSendOpen(false);
    setConfirmDeleteOpen(false);
    setSelectErrorOpen(false);
    setSendSuccessOpen(false);
    setDeleteSuccessOpen(false);
    setEpinInvalidOpen(false);
    setEventsModal({ open: false, claimId: "", events: [] });
    setAttachmentsModal({ open: false, claimId: "", rows: [] });
  };

  return (
    <div className="hv-container hv-page hv-page--dense hv-page--fill">
      <div className="hv-stack">

        {/* ─── Page header band ─── */}
        <div className="hv-pagehead">
          <div>
            <h1 className="hv-pagehead__title">Claim Approval</h1>
            <div className="hv-pagehead__sub">
              <span>Review, validate, and submit claims to the clearing house</span>
            </div>
          </div>
          <div className="hv-pagehead__actions">
            <span className="hv-pill hv-pill--neutral">{sortedClaims.length} CLAIMS</span>
          </div>
        </div>

        {/* ─── Filters card ─── */}
        <div className="hv-panel">
          <div className="hv-panel__head">
            <span className="hv-panel__heading">Filters</span>
            <span className="hv-pill hv-pill--neutral">{fmtDate(startDate)} — {fmtDate(endDate)}</span>
          </div>
          <div className="hv-panel__body">
            <div className="hv-toolbar">
              <div className="hv-toolbar__group">
                <select className="hv-select" value={dateType} onChange={(e) => setDateType(e.target.value)}>
                  {DATE_TYPES.map((d) => (
                    <option key={d.value} value={d.value}>{d.label}</option>
                  ))}
                </select>
                <label className="hv-toolbar__label">From:</label>
                <DatePicker
                  className="hv-date"
                  selected={startDate}
                  onChange={(d) => { setStartDate(d); setStartOpen(false); }}
                  dateFormat="MM/dd/yyyy"
                  maxDate={new Date()}
                  readOnly={allDates}
                  open={startOpen}
                  onFocus={() => !allDates && setStartOpen(true)}
                  onInputClick={() => !allDates && setStartOpen(true)}
                  onClickOutside={() => setStartOpen(false)}
                  renderCustomHeader={renderCalendarHeader}
                  calendarContainer={makeCalContainer("start")}
                  calendarClassName="enc-cal"
                />
                <label className="hv-toolbar__label">To:</label>
                <DatePicker
                  className="hv-date"
                  selected={endDate}
                  onChange={(d) => { setEndDate(d); setEndOpen(false); }}
                  dateFormat="MM/dd/yyyy"
                  maxDate={new Date()}
                  readOnly={allDates}
                  open={endOpen}
                  onFocus={() => !allDates && setEndOpen(true)}
                  onInputClick={() => !allDates && setEndOpen(true)}
                  onClickOutside={() => setEndOpen(false)}
                  renderCustomHeader={renderCalendarHeader}
                  calendarContainer={makeCalContainer("end")}
                  calendarClassName="enc-cal"
                />
                <label className="hv-check"><input type="checkbox" checked={allDates} onChange={handleAllDatesToggle} /> All</label>
              </div>

              <div className="hv-toolbar__group">
                <label className="hv-toolbar__label">Location:</label>
                <select className="hv-select" value={selectedLocation} onChange={(e) => setSelectedLocation(e.target.value)}>
                  <option value="-1">All</option>
                  {locations.map((loc) => (
                    <option key={loc.LocationId} value={loc.LocationId}>{loc.LocationName}</option>
                  ))}
                </select>
              </div>

              <div className="hv-toolbar__group">
                <label className="hv-toolbar__label">Search By:</label>
                <select className="hv-select" value={searchFilter} onChange={(e) => {
                  setSearchFilter(e.target.value);
                  setSearchText("");
                  setSuggestions([]);
                  setSelectedPatID("");
                  setSelectedInsID("");
                }}>
                  {searchFilters.map((f) => (
                    <option key={f.value} value={f.value}>{f.label}</option>
                  ))}
                </select>
                <input
                  className="hv-input"
                  type="text"
                  value={searchText}
                  onChange={(e) => handleSearchTextChange(e.target.value)}
                  onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                  onKeyDown={(e) => e.key === "Enter" && (setShowSuggestions(false), handleSearch())}
                  placeholder=""
                  autoComplete="off"
                />
                {showSuggestions && suggestions.length > 0 && (
                  <ul className="enc-suggest-list">
                    {suggestions.map((s, i) => (
                      <li key={`${s.val}-${i}`} onMouseDown={() => handleSuggestionSelect(s)}>
                        {s.label}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <button className="hv-icon-btn" title="Search" onClick={handleSearch}>
                <FontAwesomeIcon icon={faMagnifyingGlass} />
              </button>
            </div>

            <div className="hv-tablebar">
              <div className="hv-btn-group">
                {statusPills.map((p) => (
                  <button
                    key={p.value} type="button"
                    className={`hv-btn hv-btn--sm ${statusFilter === p.value ? "is-active" : ""}`}
                    onClick={() => applyStatusPill(p.value)}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <div className="hv-btn-group">
                <button
                  type="button"
                  className="hv-btn hv-btn--sm"
                  title="Print current view"
                  onClick={handlePrint}
                >
                  <FontAwesomeIcon icon={faPrint} />
                  <span>Print</span>
                </button>
                <button
                  type="button"
                  className="hv-btn hv-btn--sm"
                  title="Download as Excel"
                  onClick={exportToExcel}
                >
                  <FontAwesomeIcon icon={faFileExcel} />
                  <span>Excel</span>
                </button>
                <button
                  type="button"
                  className="hv-btn hv-btn--sm"
                  title="Download as PDF"
                  onClick={exportToPDF}
                >
                  <FontAwesomeIcon icon={faFilePdf} />
                  <span>PDF</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ─── Status summary card ─── */}
        <div className="hv-panel">
          <div className="hv-panel__head">
            <span className="hv-panel__heading">Claim Status Summary</span>
            <span className="hv-pill hv-pill--neutral">{countCards[0].value || 0} Total</span>
          </div>
          <div className="hv-panel__body">
            <div className="enc-counts">
              {countCards.map((c) => (
                <div
                  key={c.key}
                  className={`hv-stat ${activeCount === c.key ? "is-active" : ""}`}
                  onClick={() => applyCountCard(c.key)}
                >
                  <div className="hv-stat__body">
                    <div className="hv-stat__value">{c.value || 0}</div>
                    <div className="hv-stat__label">{c.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ─── Claims table card ─── */}
        <div className="hv-panel hv-panel--flush">
          <div className="hv-panel__head">
            <span className="hv-panel__heading">Claims to Approve</span>
            <span className="hv-pill hv-pill--neutral">{sortedClaims.length} Records</span>
          </div>

          <div className="hv-tabbar">
            <button
              type="button"
              className={`hv-tab ${activeTab === "details" ? "is-active" : ""}`}
              onClick={() => setActiveTab("details")}
            >
              <FontAwesomeIcon icon={faClipboardList} />
              Claim Details
            </button>
            <button
              type="button"
              className={`hv-tab ${activeTab === "charges" ? "is-active" : ""}`}
              onClick={() => setActiveTab("charges")}
            >
              <FontAwesomeIcon icon={faDollarSign} />
              Charges &amp; Dates
            </button>
            <button
              type="button"
              className={`hv-tab ${activeTab === "submission" ? "is-active" : ""}`}
              onClick={() => setActiveTab("submission")}
            >
              <FontAwesomeIcon icon={faPaperPlane} />
              Submission
              {(selectedSendRows.length > 0 || selectedRows.length > 0) && (
                <span className="hv-tabbar__count">
                  {selectedSendRows.length + selectedRows.length}
                </span>
              )}
            </button>
          </div>

          <div className="hv-tablebar">
            <div className="hv-toolbar__group">
              <div className="hv-panel__hint">
                <strong>{displayedClaims.length}</strong> of <strong>{sortedClaims.length}</strong> claim{sortedClaims.length === 1 ? "" : "s"}
              </div>

              <div className="hv-toolbar__group">
                <span className="hv-toolbar__label">Show</span>
                <div className="hv-btn-group">
                  {["25", "50", "75", "100", "all"].map((v) => (
                    <button
                      key={v}
                      type="button"
                      className={`hv-btn hv-btn--sm ${viewLimit === v ? "is-active" : ""}`}
                      onClick={() => setViewLimit(v)}
                    >
                      {v === "all" ? "All" : v}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="hv-toolbar__group">
              {selectedSendRows.length > 0 && (
                <span className="hv-pill hv-pill--info">
                  {selectedSendRows.length} to send
                </span>
              )}
              {selectedRows.length > 0 && (
                <span className="hv-pill hv-pill--danger">
                  {selectedRows.length} to delete
                </span>
              )}
            </div>
          </div>

          <div className="hv-table-scroll hv-table-scroll--fade">
              <table className="hv-table hv-worksheet hv-worksheet--pinned hv-table--striped" ref={tableRef}>
                <thead>
                  {activeTab === "details" && (
                    <tr>
                      <th style={{ width: 32, textAlign: "center" }}>#</th>
                      <th style={{ width: 95 }}>
                        <div className="hv-th-title">
                          <SortLink k="LocationNickname" sortConfig={sortConfig} onClick={handleSort}>Location</SortLink>
                        </div>
                      </th>
                      <th style={{ width: 100 }}>
                        <div className="hv-th-title">
                          <SortLink k="AppointmentDate" sortConfig={sortConfig} onClick={handleSort}>DOS</SortLink>
                        </div>
                        <input className="hv-cellinput" placeholder="Search"
                          value={columnSearch.dos}
                          onChange={(e) => handleColumnSearch("dos", e.target.value)} />
                      </th>
                      <th style={{ width: 150 }}>
                        <div className="hv-th-title">
                          <SortLink k="LastName" sortConfig={sortConfig} onClick={handleSort}>Patient Name</SortLink>
                        </div>
                        <input className="hv-cellinput" placeholder="Search"
                          value={columnSearch.patient}
                          onChange={(e) => handleColumnSearch("patient", e.target.value)} />
                      </th>
                      <th style={{ width: 95 }}>
                        <div className="hv-th-title">
                          <SortLink k="DOB" sortConfig={sortConfig} onClick={handleSort}>DOB</SortLink>
                        </div>
                        <input className="hv-cellinput" placeholder="Search"
                          value={columnSearch.dob}
                          onChange={(e) => handleColumnSearch("dob", e.target.value)} />
                      </th>
                      <th style={{ width: 180 }}>
                        <div className="hv-th-title">
                          <SortLink k="Payer" sortConfig={sortConfig} onClick={handleSort}>Insurance</SortLink>
                          <input
                            type="checkbox"
                            className="hv-th-check"
                            title="Select all for eligibility check"
                            checked={sortedClaims.length > 0 && insuranceChecked.length === sortedClaims.length}
                            onChange={() => toggleAll(insuranceChecked, setInsuranceChecked)}
                          />
                        </div>
                        <input className="hv-cellinput" placeholder="Search"
                          value={columnSearch.insurance}
                          onChange={(e) => handleColumnSearch("insurance", e.target.value)} />
                        <button className="hv-btn hv-btn--sm" onClick={handleCheckEligibility}>
                          Check
                        </button>
                      </th>
                      <th style={{ width: 115 }}>
                        <div className="hv-th-title">
                          <SortLink k="ClaimStatus" sortConfig={sortConfig} onClick={handleSort}>Status</SortLink>
                        </div>
                        <input className="hv-cellinput" placeholder="Search"
                          value={columnSearch.status}
                          onChange={(e) => handleColumnSearch("status", e.target.value)} />
                      </th>
                      <th style={{ width: 110 }}>
                        <div className="hv-th-title">
                          <SortLink k="ClaimId" sortConfig={sortConfig} onClick={handleSort}>Claim #</SortLink>
                        </div>
                        <input className="hv-cellinput" placeholder="Search"
                          value={columnSearch.claim}
                          onChange={(e) => handleColumnSearch("claim", e.target.value)} />
                      </th>
                      <th style={{ width: 130 }}>
                        <div className="hv-th-title">
                          <SortLink k="RenderingProvider" sortConfig={sortConfig} onClick={handleSort}>Provider</SortLink>
                        </div>
                        <input className="hv-cellinput" placeholder="Search"
                          value={columnSearch.provider}
                          onChange={(e) => handleColumnSearch("provider", e.target.value)} />
                      </th>
                      <th style={{ width: 90 }}>
                        <div className="hv-th-title">
                          <SortLink k="EncCPT" sortConfig={sortConfig} onClick={handleSort}>CPT</SortLink>
                        </div>
                        <input className="hv-cellinput" placeholder="Search"
                          value={columnSearch.cpt}
                          onChange={(e) => handleColumnSearch("cpt", e.target.value)} />
                      </th>
                      <th style={{ width: 90 }}>
                        <div className="hv-th-title">
                          <SortLink k="EncICD" sortConfig={sortConfig} onClick={handleSort}>ICD</SortLink>
                        </div>
                        <input className="hv-cellinput" placeholder="Search"
                          value={columnSearch.icd}
                          onChange={(e) => handleColumnSearch("icd", e.target.value)} />
                      </th>
                      <th style={{ width: 90 }}>
                        <div className="hv-th-title">
                          <SortLink k="EncModifier" sortConfig={sortConfig} onClick={handleSort}>Modifier</SortLink>
                        </div>
                        <input className="hv-cellinput" placeholder="Search"
                          value={columnSearch.modifier}
                          onChange={(e) => handleColumnSearch("modifier", e.target.value)} />
                      </th>
                      <th style={{ width: 80, textAlign: "center" }}>
                        <div className="hv-th-title" style={{ justifyContent: "center" }}>View</div>
                      </th>
                    </tr>
                  )}

                  {activeTab === "charges" && (
                    <tr>
                      <th style={{ width: 32, textAlign: "center" }}>#</th>
                      <th style={{ width: 95 }}>
                        <div className="hv-th-title">
                          <SortLink k="LocationNickname" sortConfig={sortConfig} onClick={handleSort}>Location</SortLink>
                        </div>
                      </th>
                      <th style={{ width: 100 }}>
                        <div className="hv-th-title">
                          <SortLink k="AppointmentDate" sortConfig={sortConfig} onClick={handleSort}>DOS</SortLink>
                        </div>
                        <input className="hv-cellinput" placeholder="Search"
                          value={columnSearch.dos}
                          onChange={(e) => handleColumnSearch("dos", e.target.value)} />
                      </th>
                      <th style={{ width: 150 }}>
                        <div className="hv-th-title">
                          <SortLink k="LastName" sortConfig={sortConfig} onClick={handleSort}>Patient Name</SortLink>
                        </div>
                        <input className="hv-cellinput" placeholder="Search"
                          value={columnSearch.patient}
                          onChange={(e) => handleColumnSearch("patient", e.target.value)} />
                      </th>
                      <th style={{ width: 95 }}>
                        <div className="hv-th-title">
                          <SortLink k="DOB" sortConfig={sortConfig} onClick={handleSort}>DOB</SortLink>
                        </div>
                        <input className="hv-cellinput" placeholder="Search"
                          value={columnSearch.dob}
                          onChange={(e) => handleColumnSearch("dob", e.target.value)} />
                      </th>
                      <th style={{ width: 110 }}>
                        <div className="hv-th-title">
                          <SortLink k="ClaimId" sortConfig={sortConfig} onClick={handleSort}>Claim #</SortLink>
                        </div>
                        <input className="hv-cellinput" placeholder="Search"
                          value={columnSearch.claim}
                          onChange={(e) => handleColumnSearch("claim", e.target.value)} />
                      </th>
                      <th style={{ width: 115 }}>
                        <div className="hv-th-title">
                          <SortLink k="ClaimTotalSubmitedCharge" sortConfig={sortConfig} onClick={handleSort}>Total Charge</SortLink>
                        </div>
                        <input className="hv-cellinput" placeholder="Search"
                          value={columnSearch.totalCharge}
                          onChange={(e) => handleColumnSearch("totalCharge", e.target.value)} />
                      </th>
                      <th style={{ width: 115 }}>
                        <div className="hv-th-title">
                          <SortLink k="SubmitDate" sortConfig={sortConfig} onClick={handleSort}>Submit Date</SortLink>
                        </div>
                        <input className="hv-cellinput" placeholder="Search"
                          value={columnSearch.submitDate}
                          onChange={(e) => handleColumnSearch("submitDate", e.target.value)} />
                      </th>

                        <th style={{ width: 115 }}>
                        <div className="hv-th-title">
                          <SortLink k="PostingDate" sortConfig={sortConfig} onClick={handleSort}>Posting Date</SortLink>
                        </div>
                        <input className="hv-cellinput" placeholder="Search"
                          value={columnSearch.PostingDate}
                          onChange={(e) => handleColumnSearch("PostingDate", e.target.value)} />
                      </th>
                      <th style={{ width: 135 }}>
                        <div className="hv-th-title">
                          <SortLink k="ReceivedCharge" sortConfig={sortConfig} onClick={handleSort}>Received Charge</SortLink>
                        </div>
                        <input className="hv-cellinput" placeholder="Search"
                          value={columnSearch.receivedCharge}
                          onChange={(e) => handleColumnSearch("receivedCharge", e.target.value)} />
                      </th>
                      <th style={{ width: 115 }}>
                        <div className="hv-th-title">
                          <SortLink k="UpdatedBy" sortConfig={sortConfig} onClick={handleSort}>Updated By</SortLink>
                        </div>
                        <input className="hv-cellinput" placeholder="Search"
                          value={columnSearch.updatedBy}
                          onChange={(e) => handleColumnSearch("updatedBy", e.target.value)} />
                      </th>
                      <th style={{ width: 125 }}>
                        <div className="hv-th-title">
                          <SortLink k="SubmittedBy" sortConfig={sortConfig} onClick={handleSort}>Submitted By</SortLink>
                        </div>
                        <input className="hv-cellinput" placeholder="Search"
                          value={columnSearch.submittedBy}
                          onChange={(e) => handleColumnSearch("submittedBy", e.target.value)}
                          autoComplete="off" />
                      </th>
                    </tr>
                  )}

                  {activeTab === "submission" && (
                    <tr>
                      <th style={{ width: 32, textAlign: "center" }}>#</th>
                      <th style={{ width: 95 }}>
                        <div className="hv-th-title">
                          <SortLink k="LocationNickname" sortConfig={sortConfig} onClick={handleSort}>Location</SortLink>
                        </div>
                      </th>
                      <th style={{ width: 100 }}>
                        <div className="hv-th-title">
                          <SortLink k="AppointmentDate" sortConfig={sortConfig} onClick={handleSort}>DOS</SortLink>
                        </div>
                        <input className="hv-cellinput" placeholder="Search"
                          value={columnSearch.dos}
                          onChange={(e) => handleColumnSearch("dos", e.target.value)} />
                      </th>
                      <th style={{ width: 150 }}>
                        <div className="hv-th-title">
                          <SortLink k="LastName" sortConfig={sortConfig} onClick={handleSort}>Patient Name</SortLink>
                        </div>
                        <input className="hv-cellinput" placeholder="Search"
                          value={columnSearch.patient}
                          onChange={(e) => handleColumnSearch("patient", e.target.value)} />
                      </th>
                      <th style={{ width: 95 }}>
                        <div className="hv-th-title">
                          <SortLink k="DOB" sortConfig={sortConfig} onClick={handleSort}>DOB</SortLink>
                        </div>
                        <input className="hv-cellinput" placeholder="Search"
                          value={columnSearch.dob}
                          onChange={(e) => handleColumnSearch("dob", e.target.value)} />
                      </th>
                      <th style={{ width: 110 }}>
                        <div className="hv-th-title">
                          <SortLink k="ClaimId" sortConfig={sortConfig} onClick={handleSort}>Claim #</SortLink>
                        </div>
                        <input className="hv-cellinput" placeholder="Search"
                          value={columnSearch.claim}
                          onChange={(e) => handleColumnSearch("claim", e.target.value)} />
                      </th>
                      <th style={{ width: 175 }}>
                        <div className="hv-th-title">Clearing House</div>
                      </th>
                      <th style={{ width: 70, textAlign: "center" }}>
                        <div className="hv-th-title" style={{ justifyContent: "center" }}>
                          <span>Send</span>
                          <input
                            type="checkbox"
                            className="hv-th-check"
                            title="Select all to send"
                            checked={sortedClaims.length > 0 && selectedSendRows.length === sortedClaims.length}
                            onChange={() => toggleAll(selectedSendRows, setSelectedSendRows)}
                          />
                        </div>
                      </th>
                      <th style={{ width: 110 }}>
                        <div className="hv-th-title">Attachments</div>
                      </th>
                      <th style={{ width: 95, textAlign: "center" }}>
                        <div className="hv-th-title" style={{ justifyContent: "center" }}>
                          <span>Validate</span>
                          <input type="checkbox" className="hv-th-check" />
                        </div>
                      </th>
                      <th style={{ width: 70, textAlign: "center" }}>
                        <div className="hv-th-title" style={{ justifyContent: "center" }}>
                          <FontAwesomeIcon icon={faTrashCan} className="hv-th-trash" onClick={openDelete} />
                          <input
                            type="checkbox"
                            className="hv-th-check"
                            title="Select all to delete"
                            checked={sortedClaims.length > 0 && selectedRows.length === sortedClaims.length}
                            onChange={() => toggleAll(selectedRows, setSelectedRows)}
                          />
                        </div>
                      </th>
                    </tr>
                  )}

                  {activeTab === "all" && (
                    <tr>
                      <th style={{ width: 32, textAlign: "center" }}>#</th>
                      <th style={{ width: 95 }}>
                        <div className="hv-th-title">
                          <SortLink k="LocationNickname" sortConfig={sortConfig} onClick={handleSort}>Location</SortLink>
                        </div>
                      </th>
                      <th style={{ width: 100 }}>
                        <div className="hv-th-title">
                          <SortLink k="AppointmentDate" sortConfig={sortConfig} onClick={handleSort}>DOS</SortLink>
                        </div>
                        <input className="hv-th-search" placeholder="Search"
                          value={columnSearch.dos}
                          onChange={(e) => handleColumnSearch("dos", e.target.value)} />
                      </th>
                      <th style={{ width: 150 }}>
                        <div className="hv-th-title">
                          <SortLink k="LastName" sortConfig={sortConfig} onClick={handleSort}>Patient Name</SortLink>
                        </div>
                        <input className="hv-th-search" placeholder="Search"
                          value={columnSearch.patient}
                          onChange={(e) => handleColumnSearch("patient", e.target.value)} />
                      </th>
                      <th style={{ width: 95 }}>
                        <div className="hv-th-title">
                          <SortLink k="DOB" sortConfig={sortConfig} onClick={handleSort}>DOB</SortLink>
                        </div>
                        <input className="hv-th-search" placeholder="Search"
                          value={columnSearch.dob}
                          onChange={(e) => handleColumnSearch("dob", e.target.value)} />
                      </th>
                      <th style={{ width: 180 }}>
                        <div className="hv-th-title">
                          <SortLink k="Payer" sortConfig={sortConfig} onClick={handleSort}>Insurance</SortLink>
                          <input
                            type="checkbox"
                            className="hv-th-check"
                            title="Select all for eligibility check"
                            checked={sortedClaims.length > 0 && insuranceChecked.length === sortedClaims.length}
                            onChange={() => toggleAll(insuranceChecked, setInsuranceChecked)}
                          />
                        </div>
                        <input className="hv-th-search" placeholder="Search"
                          value={columnSearch.insurance}
                          onChange={(e) => handleColumnSearch("insurance", e.target.value)} />
                        <button className="hv-th-check-btn" onClick={handleCheckEligibility}>
                          Check
                        </button>
                      </th>
                      <th style={{ width: 115 }}>
                        <div className="hv-th-title">
                          <SortLink k="ClaimStatus" sortConfig={sortConfig} onClick={handleSort}>Status</SortLink>
                        </div>
                        <input className="hv-th-search" placeholder="Search"
                          value={columnSearch.status}
                          onChange={(e) => handleColumnSearch("status", e.target.value)} />
                      </th>
                      <th style={{ width: 110 }}>
                        <div className="hv-th-title">
                          <SortLink k="ClaimId" sortConfig={sortConfig} onClick={handleSort}>Claim #</SortLink>
                        </div>
                        <input className="hv-th-search" placeholder="Search"
                          value={columnSearch.claim}
                          onChange={(e) => handleColumnSearch("claim", e.target.value)} />
                      </th>
                      <th style={{ width: 130 }}>
                        <div className="hv-th-title">
                          <SortLink k="RenderingProvider" sortConfig={sortConfig} onClick={handleSort}>Provider</SortLink>
                        </div>
                        <input className="hv-th-search" placeholder="Search"
                          value={columnSearch.provider}
                          onChange={(e) => handleColumnSearch("provider", e.target.value)} />
                      </th>
                      <th style={{ width: 90 }}>
                        <div className="hv-th-title">
                          <SortLink k="EncCPT" sortConfig={sortConfig} onClick={handleSort}>CPT</SortLink>
                        </div>
                        <input className="hv-th-search" placeholder="Search"
                          value={columnSearch.cpt}
                          onChange={(e) => handleColumnSearch("cpt", e.target.value)} />
                      </th>
                      <th style={{ width: 90 }}>
                        <div className="hv-th-title">
                          <SortLink k="EncICD" sortConfig={sortConfig} onClick={handleSort}>ICD</SortLink>
                        </div>
                        <input className="hv-th-search" placeholder="Search"
                          value={columnSearch.icd}
                          onChange={(e) => handleColumnSearch("icd", e.target.value)} />
                      </th>
                      <th style={{ width: 90 }}>
                        <div className="hv-th-title">
                          <SortLink k="EncModifier" sortConfig={sortConfig} onClick={handleSort}>Modifier</SortLink>
                        </div>
                        <input className="hv-th-search" placeholder="Search"
                          value={columnSearch.modifier}
                          onChange={(e) => handleColumnSearch("modifier", e.target.value)} />
                      </th>
                      <th style={{ width: 80, textAlign: "center" }}>
                        <div className="hv-th-title" style={{ justifyContent: "center" }}>View</div>
                      </th>
                      <th style={{ width: 115 }}>
                        <div className="hv-th-title">
                          <SortLink k="ClaimTotalSubmitedCharge" sortConfig={sortConfig} onClick={handleSort}>Total Charge</SortLink>
                        </div>
                        <input className="hv-th-search" placeholder="Search"
                          value={columnSearch.totalCharge}
                          onChange={(e) => handleColumnSearch("totalCharge", e.target.value)} />
                      </th>
                      <th style={{ width: 115 }}>
                        <div className="hv-th-title">
                          <SortLink k="SubmitDate" sortConfig={sortConfig} onClick={handleSort}>Submit Date</SortLink>
                        </div>
                        <input className="hv-th-search" placeholder="Search"
                          value={columnSearch.submitDate}
                          onChange={(e) => handleColumnSearch("submitDate", e.target.value)} />
                      </th>
                      <th style={{ width: 115 }}>
                        <div className="hv-th-title">
                          <SortLink k="PostingDate" sortConfig={sortConfig} onClick={handleSort}>Posting Date</SortLink>
                        </div>
                        <input className="hv-th-search" placeholder="Search"
                          value={columnSearch.PostingDate}
                          onChange={(e) => handleColumnSearch("PostingDate", e.target.value)} />
                      </th>
                      <th style={{ width: 135 }}>
                        <div className="hv-th-title">
                          <SortLink k="ReceivedCharge" sortConfig={sortConfig} onClick={handleSort}>Received Charge</SortLink>
                        </div>
                        <input className="hv-th-search" placeholder="Search"
                          value={columnSearch.receivedCharge}
                          onChange={(e) => handleColumnSearch("receivedCharge", e.target.value)} />
                      </th>
                      <th style={{ width: 115 }}>
                        <div className="hv-th-title">
                          <SortLink k="UpdatedBy" sortConfig={sortConfig} onClick={handleSort}>Updated By</SortLink>
                        </div>
                        <input className="hv-th-search" placeholder="Search"
                          value={columnSearch.updatedBy}
                          onChange={(e) => handleColumnSearch("updatedBy", e.target.value)} />
                      </th>
                      <th style={{ width: 125 }}>
                        <div className="hv-th-title">
                          <SortLink k="SubmittedBy" sortConfig={sortConfig} onClick={handleSort}>Submitted By</SortLink>
                        </div>
                        <input className="hv-th-search" placeholder="Search"
                          value={columnSearch.submittedBy}
                          onChange={(e) => handleColumnSearch("submittedBy", e.target.value)}
                          autoComplete="off" />
                      </th>
                      <th style={{ width: 175 }}>
                        <div className="hv-th-title">Clearing House</div>
                      </th>
                      <th style={{ width: 70, textAlign: "center" }}>
                        <div className="hv-th-title" style={{ justifyContent: "center" }}>
                          <span>Send</span>
                          <input
                            type="checkbox"
                            className="hv-th-check"
                            title="Select all to send"
                            checked={sortedClaims.length > 0 && selectedSendRows.length === sortedClaims.length}
                            onChange={() => toggleAll(selectedSendRows, setSelectedSendRows)}
                          />
                        </div>
                      </th>
                      <th style={{ width: 110 }}>
                        <div className="hv-th-title">Attachments</div>
                      </th>
                      <th style={{ width: 95, textAlign: "center" }}>
                        <div className="hv-th-title" style={{ justifyContent: "center" }}>
                          <span>Validate</span>
                          <input type="checkbox" className="hv-th-check" />
                        </div>
                      </th>
                      <th style={{ width: 70, textAlign: "center" }}>
                        <div className="hv-th-title" style={{ justifyContent: "center" }}>
                          <FontAwesomeIcon icon={faTrashCan} className="hv-th-trash" onClick={openDelete} />
                          <input
                            type="checkbox"
                            className="hv-th-check"
                            title="Select all to delete"
                            checked={sortedClaims.length > 0 && selectedRows.length === sortedClaims.length}
                            onChange={() => toggleAll(selectedRows, setSelectedRows)}
                          />
                        </div>
                      </th>
                    </tr>
                  )}
                </thead>

                <tbody>
                  {sortedClaims.length === 0 && (
                    <tr>
                      <td colSpan={10} className="hv-t-center hv-td-muted">
                        {loading ? "Loading..." : "No claims found for the selected filters."}
                      </td>
                    </tr>
                  )}

                  {displayedClaims.map((item, index) => {
                    const invalid = item.DataValidationMessage && item.DataValidationMessage.trim() !== "";
                    const elig = rowState[item.ClaimId] || {};
                    const isSecondary = !!item.SecPayerID;
                    const payer = isSecondary ? item.SecPayer : item.Payer;
                    const payerID = isSecondary ? item.SecPayerID : item.PayerID;
                    const policy = isSecondary ? item.SecInsPolicyNumber : item.PolicyNumber;
                    const policyColor =
                      elig.eligStatus === "Active" ? "#0e6b60" :
                      elig.eligStatus ? "#c0392b" :
                      item.PatInsStatus === "Active" ? "#0e6b60" : "#c0392b";

                    const patientCell = (
                      <td>
                        <button type="button" className="hv-td-link" onClick={() => openPatLink(item)}>
                          {item.LastName}, {item.FirstName}
                        </button>
                        {item.PatSecInscount > 0 && (
                          <FontAwesomeIcon icon={faShieldHalved} style={{ marginLeft: 6, color: "#1565c0", fontSize: 10 }} />
                        )}
                      </td>
                    );

                    const claimCell = (
                      <td>
                        <button type="button" className="hv-td-link" onClick={() => openClaimLink(item)}>
                          {item.ClaimId}
                        </button>
                      </td>
                    );

                    const viewCell = (
                      <td style={{ alignItems: "center", gap: 8, fontSize: 15}}>

                        <FontAwesomeIcon
                          icon={faFilePdf}
                          style={{ cursor: "pointer", color: "#d9534f", marginTop:'10px', marginRight: 5 }}
                          title="Open CMS-1500 PDF"
                          onClick={() => openCMSPDF(item)}
                        />
                        <FontAwesomeIcon
                          icon={faRotateRight}
                          style={{ cursor: "pointer", color: "#007bff", marginTop:'10px'}}
                          title="Regenerate CMS PDF"
                          onClick={() => regenerateCMSPDF(item)}
                        />
                        {rowState[item.ClaimId]?.regenLoading && (
                          <FontAwesomeIcon icon={faSpinner} spin style={{ color: "#4a9a48", fontSize: 12 }} />
                        )}
                      </td>
                    );

                    return (
                      <tr
                        key={index}
                        className={[invalid ? "is-invalid" : "", activeRowIndex === index ? "is-picked" : ""].filter(Boolean).join(" ")}
                        title={item.DataValidationMessage || ""}
                        onClick={() => setActiveRowIndex(index)}
                      >
                        {activeTab === "details" && (
                          <>
                            <td>{index + 1}</td>
                            <td>{item.LocationNickname}</td>
                            <td>
                              <button type="button" className="hv-td-link" onClick={() => openDOSLink(item)}>
                                {fmtDate(item.AppointmentDate)}
                              </button>
                            </td>
                            {patientCell}
                            <td>{fmtDate(item.DOB)}</td>
                            <td>
                              <div style={{ fontWeight: 600 }}>{payer}</div>
                              <div style={{ fontSize: 11 }}>{payerID}</div>
                              <div style={{ fontSize: 11, color: policyColor, fontWeight: 600 }}>{policy}</div>
                              {item.IsPrimarySkipped && (
                                <div style={{ fontSize: 10, color: "#c0392b", marginTop: 2 }}>
                                  {item.Payer} (Pri) — Skipped
                                </div>
                              )}
                              <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
                                <input
                                  type="checkbox"
                                  checked={insuranceChecked.includes(index)}
                                  onChange={() => toggleInsurance(index)}
                                  style={{ accentColor: "#1a9b8c" }}
                                />
                                {elig.eligLoading && (
                                  <FontAwesomeIcon icon={faSpinner} spin style={{ color: "#0e6b60", fontSize: 11 }} />
                                )}
                                {elig.eligStatus && (
                                  <span className={`hv-pill ${elig.eligStatus === "Active" ? "hv-pill--paid" : "hv-pill--denied"}`}>
                                    {elig.eligStatus}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td>
                              <span className={statusBadgeClass(item.ClaimStatus)}>
                                {item.ClaimStatus || "—"}
                              </span>
                            </td>
                            {claimCell}
                            <td>{item.RenderingProvider}</td>
                            <td>{(item.EncCPT || "").replace(/^,/, "")}</td>
                            <td>{item.EncICD}</td>
                            <td>{item.EncModifier}</td>
                            {viewCell}
                          </>
                        )}

                        {activeTab === "charges" && (
                          <>
                            <td>{index + 1}</td>
                            <td>{item.LocationNickname}</td>
                            <td>
                              <button type="button" className="hv-td-link" onClick={() => openDOSLink(item)}>
                                {fmtDate(item.AppointmentDate)}
                              </button>
                            </td>
                            {patientCell}
                            <td>{fmtDate(item.DOB)}</td>
                            {claimCell}
                            <td>{fmtMoney(item.ClaimTotalSubmitedCharge)}</td>
                            <td>
                              {fmtDate(item.IsPrimarySkipped ? item.SecClaimSubmiDate : item.SubmitDate)}
                            </td>
                            <td>
                                {item.SecClaimPostingDate
                              ? fmtDate(item.SecClaimPostingDate)
                              : fmtDate(item.PostingDate)}

                            </td>
                            <td className={Number(item.ReceivedCharge) > 0 ? "hv-num-col hv-num hv-t-bold hv-t-green" : "hv-num-col hv-num hv-td-muted"}>
                              {fmtMoney(item.ReceivedCharge)}
                            </td>
                            <td>{item.UpdatedBy}</td>
                            <td>{item.SubmittedBy}</td>
                          </>
                        )}

                        {activeTab === "submission" && (
                          <>
                            <td>{index + 1}</td>
                            <td>{item.LocationNickname}</td>
                            <td>
                              <button type="button" className="hv-td-link" onClick={() => openDOSLink(item)}>
                                {fmtDate(item.AppointmentDate)}
                              </button>
                            </td>
                            {patientCell}
                            <td>{fmtDate(item.DOB)}</td>
                            {claimCell}
                            <td>
                              <select
                                value={item.ClearingHouse || ""}
                                onChange={(e) => handleClearingHouseChange(index, e.target.value)}
                                style={{
                                  padding: "4px 8px", border: "1px solid #c0d8d4",
                                  borderRadius: 4, background: "#e6f6f4",
                                  fontSize: 11.5, fontFamily: "inherit", outline: "none",
                                  width: "100%",
                                }}
                              >
                                <option value="">--Select--</option>
                                {clearingHouses.map((ch) => (
                                  <option key={ch.MCHID} value={ch.MCHID}>{ch.ClearingHouseName}</option>
                                ))}
                              </select>
                            </td>
                            <td style={{ textAlign: "center" }}>
                              {canSendStatus(item.ClaimStatusInt) && (
                                <input
                                  type="checkbox"
                                  checked={selectedSendRows.includes(index)}
                                  onChange={() => toggleSend(index)}
                                  style={{ accentColor: "#1a9b8c" }}
                                />
                              )}
                            </td>
                            <td>
                              {item.Attachments ? (
                                <button type="button" className="hv-td-link" onClick={() => openAttachments(item)}>
                                  <FontAwesomeIcon icon={faPaperclip} style={{ marginRight: 4, color: "#0e6b60" }} />
                                  {item.Attachments}
                                </button>
                              ) : (
                                <span style={{ color: "#9bb3af" }}>—</span>
                              )}
                            </td>
                            <td style={{ textAlign: "center" }}>
                              <FontAwesomeIcon
                                icon={faCircleCheck}
                                style={{ color: invalid ? "#c0392b" : "#4a9a48", fontSize: 14 }}
                                title={invalid ? item.DataValidationMessage : "Validated"}
                              />
                            </td>
                            <td style={{ textAlign: "center" }}>
                              <input
                                type="checkbox"
                                checked={selectedRows.includes(index)}
                                onChange={() => toggleSelect(index)}
                                style={{ accentColor: "#1a9b8c" }}
                              />
                            </td>
                          </>
                        )}

                        {activeTab === "all" && (
                          <>
                            <td>{index + 1}</td>
                            <td>{item.LocationNickname}</td>
                            <td>
                              <button type="button" className="hv-td-link" onClick={() => openDOSLink(item)}>
                                {fmtDate(item.AppointmentDate)}
                              </button>
                            </td>
                            {patientCell}
                            <td>{fmtDate(item.DOB)}</td>
                            <td>
                              <div style={{ fontWeight: 600 }}>{payer}</div>
                              <div style={{ fontSize: 11 }}>{payerID}</div>
                              <div style={{ fontSize: 11, color: policyColor, fontWeight: 600 }}>{policy}</div>
                              {item.IsPrimarySkipped && (
                                <div style={{ fontSize: 10, color: "#c0392b", marginTop: 2 }}>
                                  {item.Payer} (Pri) — Skipped
                                </div>
                              )}
                              <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
                                <input
                                  type="checkbox"
                                  checked={insuranceChecked.includes(index)}
                                  onChange={() => toggleInsurance(index)}
                                  style={{ accentColor: "#1a9b8c" }}
                                />
                                {elig.eligLoading && (
                                  <FontAwesomeIcon icon={faSpinner} spin style={{ color: "#0e6b60", fontSize: 11 }} />
                                )}
                                {elig.eligStatus && (
                                  <span className={`hv-pill ${elig.eligStatus === "Active" ? "hv-pill--paid" : "hv-pill--denied"}`}>
                                    {elig.eligStatus}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td>
                              <span className={statusBadgeClass(item.ClaimStatus)}>
                                {item.ClaimStatus || "—"}
                              </span>
                            </td>
                            {claimCell}
                            <td>{item.RenderingProvider}</td>
                            <td>{(item.EncCPT || "").replace(/^,/, "")}</td>
                            <td>{item.EncICD}</td>
                            <td>{item.EncModifier}</td>
                            {viewCell}
                            <td>{fmtMoney(item.ClaimTotalSubmitedCharge)}</td>
                            <td>
                              {fmtDate(item.IsPrimarySkipped ? item.SecClaimSubmiDate : item.SubmitDate)}
                            </td>
                            <td>
                              {item.SecClaimPostingDate
                                ? fmtDate(item.SecClaimPostingDate)
                                : fmtDate(item.PostingDate)}
                            </td>
                            <td className={Number(item.ReceivedCharge) > 0 ? "hv-num-col hv-num hv-t-bold hv-t-green" : "hv-num-col hv-num hv-td-muted"}>
                              {fmtMoney(item.ReceivedCharge)}
                            </td>
                            <td>{item.UpdatedBy}</td>
                            <td>{item.SubmittedBy}</td>
                            <td>
                              <select
                                value={item.ClearingHouse || ""}
                                onChange={(e) => handleClearingHouseChange(index, e.target.value)}
                                style={{
                                  padding: "4px 8px", border: "1px solid #c0d8d4",
                                  borderRadius: 4, background: "#e6f6f4",
                                  fontSize: 11.5, fontFamily: "inherit", outline: "none",
                                  width: "100%",
                                }}
                              >
                                <option value="">--Select--</option>
                                {clearingHouses.map((ch) => (
                                  <option key={ch.MCHID} value={ch.MCHID}>{ch.ClearingHouseName}</option>
                                ))}
                              </select>
                            </td>
                            <td style={{ textAlign: "center" }}>
                              {canSendStatus(item.ClaimStatusInt) && (
                                <input
                                  type="checkbox"
                                  checked={selectedSendRows.includes(index)}
                                  onChange={() => toggleSend(index)}
                                  style={{ accentColor: "#1a9b8c" }}
                                />
                              )}
                            </td>
                            <td>
                              {item.Attachments ? (
                                <button type="button" className="hv-td-link" onClick={() => openAttachments(item)}>
                                  <FontAwesomeIcon icon={faPaperclip} style={{ marginRight: 4, color: "#0e6b60" }} />
                                  {item.Attachments}
                                </button>
                              ) : (
                                <span style={{ color: "#9bb3af" }}>—</span>
                              )}
                            </td>
                            <td style={{ textAlign: "center" }}>
                              <FontAwesomeIcon
                                icon={faCircleCheck}
                                style={{ color: invalid ? "#c0392b" : "#4a9a48", fontSize: 14 }}
                                title={invalid ? item.DataValidationMessage : "Validated"}
                              />
                            </td>
                            <td style={{ textAlign: "center" }}>
                              <input
                                type="checkbox"
                                checked={selectedRows.includes(index)}
                                onChange={() => toggleSelect(index)}
                                style={{ accentColor: "#1a9b8c" }}
                              />
                            </td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>

                {activeTab === "charges" && displayedClaims.length > 0 && (
                  <tfoot>
                    <tr className="hv-tr-total">
                      <td colSpan={6} className="hv-t-right hv-t-upper hv-t-xs">
                        Totals ({displayedClaims.length} claim{displayedClaims.length === 1 ? "" : "s"})
                      </td>
                      <td className="hv-num-col hv-num">
                        {fmtMoney(totals.totalCharge)}
                      </td>
                      <td colSpan={1} />
                      {/* Green only once money has actually come back — a data-driven
                          colour, not a page-invented one, so it stays inline per the
                          design system's own exception for computed values. */}
                      <td
                        className="hv-num-col hv-num"
                        style={totals.receivedCharge > 0 ? { color: "#15803d" } : undefined}
                      >
                        {fmtMoney(totals.receivedCharge)}
                      </td>
                      <td colSpan={2} />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>

          {selectedSendRows.length > 0 && sendSummary.invalid.length > 0 && (
            <div className="hv-alert hv-alert--pending" style={{ margin: "10px 14px" }}>
              <div className="hv-alert__body">
                <div className="hv-alert__text">
                  <strong>{sendSummary.invalid.length}</strong> of {selectedSendRows.length} selected claim(s) cannot be sent
                  {sendSummary.valid.length > 0 ? ` — only ${sendSummary.valid.length} will be sent.` : "."}
                  {" "}Reasons: missing clearing house, validation errors, or non-sendable status.
                </div>
              </div>
            </div>
          )}

          <div className="hv-bulkbar">
            <span className="hv-grow">
              {selectedSendRows.length > 0
                ? `${selectedSendRows.length} claim(s) selected to send${sendSummary.invalid.length > 0 ? ` (${sendSummary.valid.length} valid)` : ""}`
                : selectedRows.length > 0
                  ? `${selectedRows.length} claim(s) selected to delete`
                  : "Use the Send / Delete checkboxes per row to take bulk actions"}
            </span>
            <div className="hv-row hv-row--tight">
              <button className="hv-btn hv-btn--danger" disabled={selectedRows.length === 0} onClick={openDelete}>
                <FontAwesomeIcon icon={faTrashCan} /> DELETE
              </button>
              <button className="hv-btn hv-btn--primary" disabled={selectedSendRows.length === 0} onClick={openSend}>
                <FontAwesomeIcon icon={faPaperPlane} /> SEND TO CLEARING HOUSE
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ─── MODALS ─── */}

      {selectErrorOpen && (
        <ModalShell title="Selection required" onClose={closeAllModals} headerClass="warn">
          <p>Please select at least one claim before continuing.</p>
          <div className="hv-modal__foot">
            <button className="hv-btn hv-btn--primary" onClick={closeAllModals}>OK</button>
          </div>
        </ModalShell>
      )}

      {confirmSendOpen && (
        <ModalShell title="Send to Clearing House" onClose={closeAllModals} wide>
          <p style={{ marginBottom: 12 }}>
            You selected <strong>{selectedSendRows.length}</strong> claim(s).
            {sendSummary.valid.length > 0 && <> <strong>{sendSummary.valid.length}</strong> will be sent.</>}
            {sendSummary.invalid.length > 0 && <> <strong style={{ color: "#c0392b" }}>{sendSummary.invalid.length} will be skipped.</strong></>}
          </p>

          {sendSummary.valid.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", color: "#0e6b60", letterSpacing: ".4px", marginBottom: 6 }}>
                Summary
              </div>
              <div style={{ background: "#e6f6f4", border: "1px solid #c0d8d4", borderRadius: 4, padding: "8px 12px", fontSize: 12.5 }}>
                <div>Total charge: <strong>{fmtMoney(sendSummary.totalCharge)}</strong></div>
                {Object.keys(sendSummary.byCH).length > 0 && (
                  <ul style={{ margin: "6px 0 0", paddingLeft: 18, lineHeight: 1.6 }}>
                    {Object.entries(sendSummary.byCH).map(([name, info]) => (
                      <li key={name}>
                        <strong>{name}</strong>: {info.count} claim(s) — {fmtMoney(info.total)}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {sendSummary.invalid.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", color: "#c0392b", letterSpacing: ".4px", marginBottom: 6 }}>
                Skipped ({sendSummary.invalid.length})
              </div>
              <div style={{ background: "#fdecea", border: "1px solid #e53935", borderRadius: 4, padding: "8px 12px", fontSize: 12, maxHeight: 200, overflowY: "auto" }}>
                <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
                  {sendSummary.invalid.map(({ item, reasons }) => (
                    <li key={item.ClaimId}>
                      <strong>{item.ClaimId}</strong> ({item.LastName}, {item.FirstName})
                      — <span style={{ color: "#9b1a1a" }}>{reasons.join(" + ")}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {sendSummary.valid.length === 0 && sendSummary.invalid.length > 0 && (
            <div className="hv-alert hv-alert--denied" style={{ marginBottom: 12 }}>
              <div className="hv-alert__body">
                <div className="hv-alert__text">
                  All selected claims have issues. Fix the issues or unselect those rows before sending.
                </div>
              </div>
            </div>
          )}

          <div className="hv-modal__foot">
            <button className="hv-btn hv-btn--ghost" onClick={closeAllModals}>Cancel</button>
            <button
              className="hv-btn hv-btn--primary"
              onClick={handleSendToCH}
              disabled={sendSummary.valid.length === 0}
            >
              SEND {sendSummary.valid.length} CLAIM{sendSummary.valid.length === 1 ? "" : "S"}
            </button>
          </div>
        </ModalShell>
      )}

      {sendSuccessOpen && (
        <ModalShell title="Send successful" onClose={closeAllModals} headerClass="ok">
          <p>{sendSuccessCount} claim(s) successfully saved for approval.</p>
          <div className="hv-modal__foot">
            <button className="hv-btn hv-btn--primary" onClick={closeAllModals}>OK</button>
          </div>
        </ModalShell>
      )}

      {confirmDeleteOpen && (
        <ModalShell title="Delete claims" onClose={closeAllModals} headerClass="warn">
          <p>Enter your EPIN to delete <strong>{selectedRows.length}</strong> claim(s).</p>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type={showEpin ? "text" : "password"}
              value={epin}
              onChange={(e) => setEpin(e.target.value)}
              maxLength={4}
              placeholder="4-digit EPIN"
              className="hv-input"
              style={{ width: 200 }}
            />
            <FontAwesomeIcon
              icon={showEpin ? faEye : faEyeSlash}
              style={{ cursor: "pointer", color: "#556b68" }}
              onClick={() => setShowEpin((v) => !v)}
            />
          </div>
          <div className="hv-modal__foot">
            <button className="hv-btn hv-btn--ghost" onClick={closeAllModals}>Cancel</button>
            <button className="hv-btn hv-btn--soft" onClick={() => handleDelete(0)} disabled={!epin}>Delete from Page</button>
            <button className="hv-btn hv-btn--danger" onClick={() => handleDelete(1)} disabled={!epin}>Delete Permanently</button>
          </div>
        </ModalShell>
      )}

      {deleteSuccessOpen && (
        <ModalShell title="Delete successful" onClose={closeAllModals} headerClass="ok">
          <p>{deleteSuccessCount} claim(s) successfully removed.</p>
          <div className="hv-modal__foot">
            <button className="hv-btn hv-btn--primary" onClick={closeAllModals}>OK</button>
          </div>
        </ModalShell>
      )}

      {epinInvalidOpen && (
        <ModalShell title="Invalid EPIN" onClose={closeAllModals} headerClass="warn">
          <p>The EPIN you entered is invalid. Please try again.</p>
          <div className="hv-modal__foot">
            <button className="hv-btn hv-btn--primary" onClick={closeAllModals}>OK</button>
          </div>
        </ModalShell>
      )}

      {eventsModal.open && (
        <ModalShell title={`Claim ${eventsModal.claimId} — Events`} onClose={closeAllModals} wide>
          {eventsModal.events.length === 0 ? (
            <p style={{ color: "#556b68" }}>No events recorded.</p>
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
              {eventsModal.events.map((ev, i) => (
                <React.Fragment key={i}>
                  <div style={{
                    background: "#e6f6f4", border: "1px solid #c0d8d4", borderRadius: 4,
                    padding: "6px 10px", fontSize: 12,
                  }}>
                    <div style={{ fontWeight: 700, color: "#0e6b60" }}>{ev.DataCurStatus}</div>
                    <div style={{ fontSize: 11, color: "#556b68" }}>
                      {fmtDate(ev.EventDateTime)} — {ev.UserFName}
                    </div>
                  </div>
                  {i < eventsModal.events.length - 1 && (
                    <FontAwesomeIcon icon={faArrowRight} style={{ color: "#0e6b60" }} />
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
          <div className="hv-modal__foot">
            <button className="hv-btn hv-btn--primary" onClick={closeAllModals}>Close</button>
          </div>
        </ModalShell>
      )}

      {attachmentsModal.open && (
        <ModalShell title={`Claim ${attachmentsModal.claimId} — Attachments`} onClose={closeAllModals} wide>
          {attachmentsModal.rows.length === 0 ? (
            <p style={{ color: "#556b68" }}>No attachments.</p>
          ) : (
            <div className="hv-table-scroll hv-table-scroll--fade">
              <table className="hv-table hv-table--striped">
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>#</th>
                    <th>Document</th>
                    <th>Preview</th>
                    <th>Uploaded</th>
                    <th>Type</th>
                    <th>Method</th>
                    <th>ACN</th>
                  </tr>
                </thead>
                <tbody>
                  {attachmentsModal.rows.map((r, i) => (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      <td>{r.DocumentName}</td>
                      <td>
                        <FontAwesomeIcon icon={faFilePdf}
                          style={{ color: "#c0392b", cursor: "pointer" }}
                          onClick={() => window.open(r.FileUrl, "_blank")} />
                      </td>
                      <td>{fmtDate(r.UploadedDate)}</td>
                      <td>{r.PWK01}</td>
                      <td>{r.PWK02}</td>
                      <td>{r.PWK06}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="hv-modal__foot">
            <button className="hv-btn hv-btn--primary" onClick={closeAllModals}>Close</button>
          </div>
        </ModalShell>
      )}

      {loading && (
        <div className="hv-loading-overlay">
          <div className="hv-spinner" />
        </div>
      )}
    </div>
  
  
  );
};

const ModalShell = ({ title, children, onClose, headerClass = "", wide = false }) => (
  <>
    <div className="hv-scrim" onClick={onClose} />
    <div className={`hv-modal ${wide ? "hv-modal--wide" : ""}`}>
      <div className="hv-modal__box">
        <div className={`hv-modal__head ${headerClass ? `hv-modal__head--${headerClass}` : ""}`}>
          <span className="hv-modal__title">{title}</span>
          <button className="hv-modal__close" onClick={onClose}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
        <div className="hv-modal__body">{children}</div>
      </div>
    </div>
  </>
);

export default ClaimApproval;